/**
 * DietSection - Diet preferences settings
 * Created by neo on 21.01.2026
 */
import * as React from 'react';
import { useCallback, useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { useSaveAthlete } from '../../Hooks/useAthleteMutations';
import { getTagValues, setTagCategoryValues } from '../../Model/Athlete/Athlete';
import { Theme } from '../../Theme/Theme';
import { Button } from '../../Components/Button';

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CardHeader = styled.div`
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
`;

const SectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${Theme.Colors.warmGrey};
  margin: 24px 0 16px 0;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

const OptionItem = styled.button<{ $selected?: boolean; $special?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 12px;
  background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : 'white')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  min-height: 52px;
  position: relative;
  ${(props) => props.$special && `grid-column: 1 / -1;`}

  &:hover {
    border-color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
    background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : Theme.Colors.lightestGrey)};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &::before,
  &::after {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
    transition-delay: 0s;
    position: absolute;
    z-index: 2;
  }

  &::before {
    content: '';
    left: 24px;
    bottom: calc(100% + 4px);
    border-width: 6px;
    border-style: solid;
    border-color: ${Theme.Colors.textColor} transparent transparent transparent;
    transform: translateY(4px);
  }

  &::after {
    content: attr(data-tooltip);
    left: 16px;
    right: 16px;
    bottom: calc(100% + 14px);
    padding: 8px 10px;
    border-radius: 8px;
    background: ${Theme.Colors.textColor};
    color: white;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    white-space: normal;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
    transform: translateY(4px);
  }

  &[data-tooltip]:hover::before,
  &[data-tooltip]:hover::after,
  &[data-tooltip]:focus-visible::before,
  &[data-tooltip]:focus-visible::after {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    transition-delay: 0.2s;
  }
`;

const OptionIcon = styled.span<{ $selected?: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.textColor)};
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

const OptionLabel = styled.span<{ $selected?: boolean }>`
  flex: 1;
  min-width: 0;
  display: block;
  font-size: 14px;
  font-weight: ${(props) => (props.$selected ? '600' : '500')};
  color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.textColor)};
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Checkbox = styled.div<{ $selected?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
  background: ${(props) => (props.$selected ? Theme.Colors.primary : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    color: white;
  }
`;

const SaveButton = styled(Button)`
  margin-top: 20px;
`;

const SuccessMessage = styled.div`
  color: ${Theme.Colors.primary};
  font-size: 14px;
  margin-top: 8px;
`;

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const BaseDietIcon = ({ children }: { children: React.ReactNode }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const EatEverythingIcon = () => (
  <BaseDietIcon>
    <path d="M4 14h16" />
    <path d="M7 14a5 5 0 0 1 10 0" />
    <path d="M12 9V7" />
    <path d="M3 18h18" />
  </BaseDietIcon>
);

const VeganIcon = () => (
  <BaseDietIcon>
    <path d="M6 14c0-5 4-9 10-9 0 6-4 10-9 10" />
    <path d="M7 16c2-2 5-5 9-7" />
  </BaseDietIcon>
);

const VegetarianIcon = () => (
  <BaseDietIcon>
    <path d="M14 6l2-2" />
    <path d="M16 8l3-1" />
    <path d="M13 7l4 4-8 9-4-4 8-9z" />
    <path d="M8 16l3 3" />
  </BaseDietIcon>
);

const PescatarianIcon = () => (
  <BaseDietIcon>
    <path d="M3 12c2-3 5-4 9-4 3 0 5 1 7 4-2 3-4 4-7 4-4 0-7-1-9-4z" />
    <path d="M18 12l3-2v4l-3-2z" />
    <circle cx="9" cy="11" r="1" />
  </BaseDietIcon>
);

const KetoIcon = () => (
  <BaseDietIcon>
    <path d="M8 7c-3 0-5 2.5-5 5.5S5 18 8 18c1.7 0 2.9-.6 3.9-1.6 1 .9 2.3 1.6 4.1 1.6 3 0 5-2.5 5-5.5S19 7 16 7c-1.8 0-3.1.7-4.1 1.6C10.9 7.6 9.7 7 8 7z" />
    <circle cx="10" cy="12.5" r="1.5" />
  </BaseDietIcon>
);

const PaleoIcon = () => (
  <BaseDietIcon>
    <path d="M10 7a5 5 0 0 1 7 7l-2 2a5 5 0 0 1-7-7z" />
    <path d="M8 16l-2 2" />
    <path d="M5.5 20.5l1-1" />
    <path d="M3.5 18.5l1-1" />
  </BaseDietIcon>
);

const HalalIcon = () => (
  <BaseDietIcon>
    <path d="M15 4a7 7 0 1 0 0 16 6.5 6.5 0 1 1 0-16z" />
    <path d="M17.5 8.5l.6 1.2 1.3.2-.9.9.2 1.3-1.2-.6-1.2.6.2-1.3-.9-.9 1.3-.2z" />
  </BaseDietIcon>
);

const HighProteinIcon = () => (
  <BaseDietIcon>
    <path d="M4 10v4" />
    <path d="M7 8v8" />
    <path d="M17 8v8" />
    <path d="M20 10v4" />
    <path d="M7 12h10" />
  </BaseDietIcon>
);

const HighFiberIcon = () => (
  <BaseDietIcon>
    <path d="M12 21V8" />
    <path d="M9 6c2 1 3 3 3 5" />
    <path d="M15 6c-2 1-3 3-3 5" />
    <path d="M8 10c2 .8 3 2.2 4 4" />
    <path d="M16 10c-2 .8-3 2.2-4 4" />
    <path d="M7 14c2 .6 3 1.8 4 3" />
    <path d="M17 14c-2 .6-3 1.8-4 3" />
  </BaseDietIcon>
);

const LowCarbIcon = () => (
  <BaseDietIcon>
    <path d="M6 10a4 4 0 0 1 12 0v7a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z" />
    <path d="M8 13h8" />
  </BaseDietIcon>
);

const LowFatIcon = () => (
  <BaseDietIcon>
    <path d="M12 3s5 5.5 5 9a5 5 0 0 1-10 0c0-3.5 5-9 5-9z" />
  </BaseDietIcon>
);

const LowCalorieIcon = () => (
  <BaseDietIcon>
    <path d="M12 3c2 3 4 5 4 8a4 4 0 1 1-8 0c0-2 1-4 4-8z" />
    <path d="M12 12c1 1 2 2 2 4a2 2 0 1 1-4 0c0-1 1-2 2-4z" />
  </BaseDietIcon>
);

const LowSugarIcon = () => (
  <BaseDietIcon>
    <rect x="6" y="6" width="12" height="12" rx="2" />
    <path d="M10 6v12" />
    <path d="M6 10h12" />
  </BaseDietIcon>
);

const LowSodiumIcon = () => (
  <BaseDietIcon>
    <path d="M9 4h6" />
    <path d="M10 4v3" />
    <path d="M14 4v3" />
    <path d="M8 7h8l-1 12H9L8 7z" />
    <path d="M11 11h2" />
    <path d="M11 14h2" />
  </BaseDietIcon>
);

const GlutenFreeIcon = () => (
  <BaseDietIcon>
    <path d="M12 21V8" />
    <path d="M9 6c2 1 3 3 3 5" />
    <path d="M15 6c-2 1-3 3-3 5" />
    <path d="M8 10c2 .8 3 2.2 4 4" />
    <path d="M16 10c-2 .8-3 2.2-4 4" />
    <path d="M5 19L19 5" />
  </BaseDietIcon>
);

const LactoseFreeIcon = () => (
  <BaseDietIcon>
    <path d="M10 4h4" />
    <path d="M11 4v3l-2 3 1 9h4l1-9-2-3V4" />
    <path d="M5 19L19 5" />
  </BaseDietIcon>
);

const DairyFreeIcon = () => (
  <BaseDietIcon>
    <path d="M7 8 5 6v4" />
    <path d="M17 8l2-2v4" />
    <path d="M8 9a4 4 0 0 1 8 0v3a4 4 0 0 1-8 0z" />
    <path d="M10 15h4" />
    <circle cx="10" cy="11" r="0.5" />
    <circle cx="14" cy="11" r="0.5" />
  </BaseDietIcon>
);

const NutFreeIcon = () => (
  <BaseDietIcon>
    <path d="M10 5c-2.2 0-4 1.8-4 4 0 1.3.6 2.5 1.6 3.2A4.5 4.5 0 0 0 6 15c0 2.2 1.8 4 4 4 1.4 0 2.6-.6 3.4-1.6.8 1 2 1.6 3.4 1.6 2.2 0 4-1.8 4-4 0-1.3-.6-2.5-1.6-3.2 1-.7 1.6-1.9 1.6-3.2 0-2.2-1.8-4-4-4-1.4 0-2.6.6-3.4 1.6C12.6 5.6 11.4 5 10 5z" />
    <path d="M5 19L19 5" />
  </BaseDietIcon>
);

const EggFreeIcon = () => (
  <BaseDietIcon>
    <path d="M12 4c3 0 5 4.5 5 8a5 5 0 1 1-10 0c0-3.5 2-8 5-8z" />
    <path d="M5 19L19 5" />
  </BaseDietIcon>
);

const SoyFreeIcon = () => (
  <BaseDietIcon>
    <path d="M13 6c3 0 5 2.2 5 5s-2 5-5 5c-2 0-4 1-5 3-1-1-2-2.3-2-4 0-3.9 3.1-9 7-9z" />
    <path d="M10 9c1 1 1.5 2.2 1.5 3.5S11 15 10 16" />
    <path d="M5 19L19 5" />
  </BaseDietIcon>
);

const EAT_EVERYTHING = 'eat_everything';

type DietOption = {
  label: string;
  value: string;
  icon: React.ReactNode;
};

const normalizeDietValue = (value: string): string | null => {
  const normalizations: Record<string, string | null> = {
    'lactose-free': 'lactose_free',
    'gluten-free': 'gluten_free',
    'dairy-free': 'dairy_free',
    'low-carb': 'low_carb',
    'low-fat': 'low_fat',
    'low-sugar': 'low_sugar',
    'high-protein': 'high_protein',
    'low-calorie': 'low_calorie',
    'gluten free': 'gluten_free',
    'dairy free': 'dairy_free',
    glutenfree: 'gluten_free',
    pescetarian: 'pescatarian',
    'I eat everything': null,
    eat_everything: null,
  };
  const normalized = normalizations[value];
  return normalized !== undefined ? normalized : value;
};

const mutualExclusions: Record<string, string[]> = {
  vegan: ['vegetarian', 'pescatarian', 'keto', 'paleo'],
  vegetarian: ['vegan', 'pescatarian', 'keto', 'paleo'],
  pescatarian: ['vegan', 'vegetarian'],
  keto: ['vegan', 'vegetarian', 'low_fat', 'high_fiber'],
  paleo: ['vegan', 'vegetarian'],
  low_fat: ['keto'],
  dairy_free: ['lactose_free'],
};

const implications: Record<string, string[]> = {
  vegan: ['dairy_free', 'egg_free'],
  dairy_free: ['lactose_free'],
};

type DietOptionButtonProps = {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  selected: boolean;
  special?: boolean;
};

const DietOptionButton: React.FC<DietOptionButtonProps> = ({ icon, label, onClick, selected, special }) => {
  const labelRef = React.useRef<HTMLSpanElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const updateTooltipVisibility = useCallback(() => {
    const labelElement = labelRef.current;

    if (!labelElement) {
      return;
    }

    setShowTooltip(labelElement.scrollWidth > labelElement.clientWidth);
  }, []);

  React.useEffect(() => {
    updateTooltipVisibility();

    const labelElement = labelRef.current;

    if (!labelElement) {
      return;
    }

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateTooltipVisibility) : null;

    resizeObserver?.observe(labelElement);
    window.addEventListener('resize', updateTooltipVisibility);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateTooltipVisibility);
    };
  }, [label, updateTooltipVisibility]);

  return (
    <OptionItem $selected={selected} onClick={onClick} data-tooltip={showTooltip ? label : undefined} $special={special}>
      <OptionIcon $selected={selected}>{icon}</OptionIcon>
      <OptionLabel ref={labelRef} $selected={selected}>
        {label}
      </OptionLabel>
      <Checkbox $selected={selected}>{selected && <CheckIcon />}</Checkbox>
    </OptionItem>
  );
};

export const DietSection: React.FC = observer(() => {
  const { t } = useTrans();
  const { authentication } = useRootStore();
  const athlete = authentication.athlete;

  const normalizedDiets = useMemo(() => {
    const rawDiets = athlete ? getTagValues(athlete, 'diet') : [];
    return rawDiets.map(normalizeDietValue).filter((v): v is string => v !== null);
  }, [athlete]);

  const [selectedDiets, setSelectedDiets] = useState<string[]>(normalizedDiets);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const eatEverythingLabel = t('explore.dietTab.searchParams.diet.all', { defaultValue: 'I Eat Everything' });

  const dietTypes: DietOption[] = [
    { label: t('explore.dietTab.searchParams.diet.vegan', { defaultValue: 'Vegan' }), value: 'vegan', icon: <VeganIcon /> },
    {
      label: t('explore.dietTab.searchParams.diet.vegetarian', { defaultValue: 'Vegetarian' }),
      value: 'vegetarian',
      icon: <VegetarianIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.pescetarian', { defaultValue: 'Pescetarian' }),
      value: 'pescatarian',
      icon: <PescatarianIcon />,
    },
    { label: t('explore.dietTab.searchParams.diet.keto', { defaultValue: 'Keto' }), value: 'keto', icon: <KetoIcon /> },
    { label: t('explore.dietTab.searchParams.diet.paleo', { defaultValue: 'Paleo' }), value: 'paleo', icon: <PaleoIcon /> },
    { label: t('explore.dietTab.searchParams.diet.halal', { defaultValue: 'Halal' }), value: 'halal', icon: <HalalIcon /> },
  ];

  const nutritionalOptions: DietOption[] = [
    {
      label: t('explore.dietTab.searchParams.diet.high_protein', { defaultValue: 'High Protein' }),
      value: 'high_protein',
      icon: <HighProteinIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.high_fiber', { defaultValue: 'High Fiber' }),
      value: 'high_fiber',
      icon: <HighFiberIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.low_carb', { defaultValue: 'Low Carb' }),
      value: 'low_carb',
      icon: <LowCarbIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.low_fat', { defaultValue: 'Low Fat' }),
      value: 'low_fat',
      icon: <LowFatIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.low_calorie', { defaultValue: 'Low Calorie' }),
      value: 'low_calorie',
      icon: <LowCalorieIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.low_sugar', { defaultValue: 'Low Sugar' }),
      value: 'low_sugar',
      icon: <LowSugarIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.low_sodium', { defaultValue: 'Low Sodium' }),
      value: 'low_sodium',
      icon: <LowSodiumIcon />,
    },
  ];

  const exclusionOptions: DietOption[] = [
    {
      label: t('explore.dietTab.searchParams.diet.gluten_free', { defaultValue: 'Gluten Free' }),
      value: 'gluten_free',
      icon: <GlutenFreeIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.lactose_free', { defaultValue: 'Lactose Free' }),
      value: 'lactose_free',
      icon: <LactoseFreeIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.dairy_free', { defaultValue: 'Dairy Free' }),
      value: 'dairy_free',
      icon: <DairyFreeIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.nut_free', { defaultValue: 'Nut Free' }),
      value: 'nut_free',
      icon: <NutFreeIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.egg_free', { defaultValue: 'Egg Free' }),
      value: 'egg_free',
      icon: <EggFreeIcon />,
    },
    {
      label: t('explore.dietTab.searchParams.diet.soy_free', { defaultValue: 'Soy Free' }),
      value: 'soy_free',
      icon: <SoyFreeIcon />,
    },
  ];

  const isEatEverything = selectedDiets.length === 0;

  const handleToggleDiet = useCallback(
    (diet: string) => {
      if (diet === EAT_EVERYTHING) {
        setSelectedDiets([]);
      } else {
        setSelectedDiets((prev) => {
          let updated = prev.filter((d) => d !== EAT_EVERYTHING);

          if (updated.includes(diet)) {
            updated = updated.filter((d) => d !== diet);
          } else {
            const toRemove = mutualExclusions[diet] ?? [];
            updated = updated.filter((d) => !toRemove.includes(d));
            updated = [...updated, diet];

            const toAdd = implications[diet] ?? [];
            toAdd.forEach((implied) => {
              if (!updated.includes(implied)) {
                updated.push(implied);
              }
            });
          }

          return updated;
        });
      }
    },
    [],
  );

  const saveAthlete = useSaveAthlete();

  const handleSave = useCallback(async () => {
    if (!athlete) return;
    setSaving(true);
    setSaved(false);
    try {
      const updatedAthlete = { ...athlete, tags: setTagCategoryValues(athlete.tags, 'diet', selectedDiets) };
      await saveAthlete.mutateAsync(updatedAthlete);
      // Update the store's athlete tags in place
      runInAction(() => {
        if (authentication.athlete) {
          authentication.athlete.tags = updatedAthlete.tags;
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }, [athlete, selectedDiets, saveAthlete, authentication]);

  const isSelected = (value: string) => selectedDiets.includes(value);

  return (
    <SectionContent>
      <SettingsCard>
        <CardHeader>
          <CardTitle>{t('settings.diet', { defaultValue: 'Diet' })}</CardTitle>
          <CardDescription>
            {t('editDietScreen.description', {
              defaultValue: 'Select your dietary preferences to personalize your recipe recommendations.',
            })}
          </CardDescription>
        </CardHeader>

        {/* "I Eat Everything" option */}
        <OptionsGrid>
          <DietOptionButton
            icon={<EatEverythingIcon />}
            label={eatEverythingLabel}
            onClick={() => handleToggleDiet(EAT_EVERYTHING)}
            selected={isEatEverything}
            special
          />
        </OptionsGrid>

        {/* Diet Types */}
        <SectionTitle>{t('editDietScreen.sections.dietTypes', { defaultValue: 'Diet Types' })}</SectionTitle>
        <OptionsGrid>
          {dietTypes.map((option) => {
            const selected = isSelected(option.value);
            return (
              <DietOptionButton
                key={option.value}
                icon={option.icon}
                label={option.label}
                onClick={() => handleToggleDiet(option.value)}
                selected={selected}
              />
            );
          })}
        </OptionsGrid>

        {/* Nutritional Preferences */}
        <SectionTitle>
          {t('editDietScreen.sections.nutritional', { defaultValue: 'Nutritional Preferences' })}
        </SectionTitle>
        <OptionsGrid>
          {nutritionalOptions.map((option) => {
            const selected = isSelected(option.value);
            return (
              <DietOptionButton
                key={option.value}
                icon={option.icon}
                label={option.label}
                onClick={() => handleToggleDiet(option.value)}
                selected={selected}
              />
            );
          })}
        </OptionsGrid>

        {/* Exclusions & Allergies */}
        <SectionTitle>
          {t('editDietScreen.sections.exclusions', { defaultValue: 'Exclusions & Allergies' })}
        </SectionTitle>
        <OptionsGrid>
          {exclusionOptions.map((option) => {
            const selected = isSelected(option.value);
            return (
              <DietOptionButton
                key={option.value}
                icon={option.icon}
                label={option.label}
                onClick={() => handleToggleDiet(option.value)}
                selected={selected}
              />
            );
          })}
        </OptionsGrid>

        <SaveButton color="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving
            ? t('common.saving', { defaultValue: 'Saving...' })
            : t('editProfileScreen.saveChanges', { defaultValue: 'Save Changes' })}
        </SaveButton>
        {saved && (
          <SuccessMessage>
            {t('editDietScreen.dietUpdateSuccess', { defaultValue: 'Diet updated successfully!' })}
          </SuccessMessage>
        )}
      </SettingsCard>
    </SectionContent>
  );
});
